import Grid from "../../utils/Grid";
import React, { useState, Suspense, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Text, PerspectiveCamera } from "@react-three/drei";
import * as THREE from "three";
import Box from "@mui/material/Box";
import { Color, Vector3 } from "three";
import { Helmet } from "react-helmet";
import { ArrowAxes } from "./ArrowAxes";
import { linkprops } from "./types";
import Controls from "./Controls";
import { threeLinkPlanarArmLinkList } from "./armTemplate";

const compareTwoLinkList = (curr: linkprops[], snapshot: linkprops[]) => {
  for (let i = 0; i < snapshot.length; i++) {
    if (i + 1 > curr.length) return i;
    else if (
      snapshot[i].d != curr[i].d ||
      snapshot[i].theta != curr[i].theta ||
      snapshot[i].a != curr[i].a ||
      snapshot[i].alpha != curr[i].alpha
    )
      return i;
  }
  return -1;
};

interface SceneProps {
  linkList: linkprops[];
  setLinkList: React.Dispatch<React.SetStateAction<linkprops[]>>;
  animationing: boolean;
  setAnimationing: React.Dispatch<React.SetStateAction<boolean>>;
  linkListSnapshot: linkprops[];
  lastTotalTransList: THREE.Matrix4[];
  setLastTotalTransList: React.Dispatch<React.SetStateAction<THREE.Matrix4[]>>;
  setEndEffectorPos: React.Dispatch<React.SetStateAction<THREE.Vector3>>;
}

function Scene({
  linkList,
  setLinkList,
  animationing,
  setAnimationing,
  linkListSnapshot,
  lastTotalTransList,
  setLastTotalTransList,
  setEndEffectorPos,
}: SceneProps) {
  const bgColor = new Color(0xecf0f1);
  const [transDList, setTransDList] = useState<THREE.Matrix4[]>([]);
  const [transThetaList, setTransThetaList] = useState<THREE.Matrix4[]>([]);
  const [transAList, setTransAList] = useState<THREE.Matrix4[]>([]);
  const [transAlphaList, setTransAlphaList] = useState<THREE.Matrix4[]>([]);

  useFrame(() => {
    if (animationing) {
      const diffindex = compareTwoLinkList(linkList, linkListSnapshot);
      if (diffindex != -1) {
        if (diffindex + 1 > linkList.length)
          setLinkList([
            ...linkList,
            {
              d: 0,
              theta: 0,
              a: 0,
              alpha: 0,
              type: linkListSnapshot[diffindex].type,
            },
          ]);
        else {
          const goldenLink = linkListSnapshot[diffindex];
          const newLink = { ...linkList[diffindex] };
          if (goldenLink.alpha > newLink.alpha) {
            newLink.alpha += 1;
          } else if (goldenLink.alpha < newLink.alpha) {
            newLink.alpha -= 1;
          } else if (goldenLink.a > newLink.a) {
            newLink.a += 0.2;
            newLink.a = parseFloat(newLink.a.toFixed(1));
          } else if (goldenLink.a < newLink.a) {
            newLink.a -= 0.2;
            newLink.a = parseFloat(newLink.a.toFixed(1));
          } else if (goldenLink.theta > newLink.theta) {
            newLink.theta += 1;
          } else if (goldenLink.theta < newLink.theta) {
            newLink.theta -= 1;
          } else if (goldenLink.d > newLink.d) {
            newLink.d += 0.2;
            newLink.d = parseFloat(newLink.d.toFixed(1));
          } else if (goldenLink.d < newLink.d) {
            newLink.d -= 0.2;
            newLink.d = parseFloat(newLink.d.toFixed(1));
          }
          const newLinkList = [...linkList];
          newLinkList[diffindex] = newLink;
          setLinkList(newLinkList);
        }
      } else {
        setAnimationing(false);
      }
    }
  });

  useEffect(() => {
    const currentDTranslist: THREE.Matrix4[] = [];
    const currentThetaTranslist: THREE.Matrix4[] = [];
    const currentATranslist: THREE.Matrix4[] = [];
    const currentAlphaTranslist: THREE.Matrix4[] = [];
    const currentLastTotalTransList: THREE.Matrix4[] = [];
    for (let i = 0; i < linkList.length; i++) {
      const link = linkList[i];
      const dtrans = new THREE.Matrix4();
      const thetatrans = new THREE.Matrix4();
      const atrans = new THREE.Matrix4();
      const alphatrans = new THREE.Matrix4();
      const theta = (link.theta * Math.PI) / 180.0;
      const alpha = (link.alpha * Math.PI) / 180.0;
      dtrans.set(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, Number(link.d), 0, 0, 0, 1);
      thetatrans.set(
        Math.cos(theta),
        -Math.sin(theta),
        0,
        0,
        Math.sin(theta),
        Math.cos(theta),
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        1
      );
      atrans.set(1, 0, 0, Number(link.a), 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1);
      alphatrans.set(
        1,
        0,
        0,
        0,
        0,
        Math.cos(alpha),
        -Math.sin(alpha),
        0,
        0,
        Math.sin(alpha),
        Math.cos(alpha),
        0,
        0,
        0,
        0,
        1
      );
      currentDTranslist.push(dtrans);
      currentThetaTranslist.push(thetatrans);
      currentATranslist.push(atrans);
      currentAlphaTranslist.push(alphatrans);

      if (i == 0) {
        currentLastTotalTransList.push(new THREE.Matrix4());
      } else {
        const totalLastTrans = currentLastTotalTransList[i - 1].clone();
        totalLastTrans
          .multiply(currentDTranslist[i - 1])
          .multiply(currentThetaTranslist[i - 1])
          .multiply(currentATranslist[i - 1])
          .multiply(currentAlphaTranslist[i - 1]);

        currentLastTotalTransList.push(totalLastTrans);
      }
    }
    setTransDList(currentDTranslist);
    setTransThetaList(currentThetaTranslist);
    setTransAList(currentATranslist);
    setTransAlphaList(currentAlphaTranslist);
    setLastTotalTransList(currentLastTotalTransList);
  }, [linkList]);

  return (
    <>
      <PerspectiveCamera
        makeDefault
        up={[0, 0, 1]}
        fov={50}
        position={[50, 50, 50]}
        rotation={[60, 0, 0]}
      />
      <color attach="background" args={[bgColor.r, bgColor.g, bgColor.b]} />
      <Suspense
        fallback={
          <Text
            color="white" // default
            anchorX="center" // default
            anchorY="middle" // default
          >
            Loading
          </Text>
        }
      >
        <OrbitControls />
        <directionalLight intensity={0.5} position={[6, 2, 1]} />
        <Grid size={100} />

        {linkList.length == lastTotalTransList.length &&
          lastTotalTransList.map((lastTrans, index) => {
            const link = linkList[index];
            const basePosition = new THREE.Vector3(0, 0, 0).applyMatrix4(
              lastTrans
            );
            const revoluteMiddlePointTrans =
              link.type == "revolute"
                ? lastTrans
                    .clone()
                    .multiply(transThetaList[index])
                    .multiply(transAList[index])
                    .multiply(transAlphaList[index])
                : lastTrans
                    .clone()
                    .multiply(transDList[index])
                    .multiply(transThetaList[index])
                    .multiply(transAlphaList[index]);
            const currTrans = lastTrans
              .clone()
              .multiply(transDList[index])
              .multiply(transThetaList[index])
              .multiply(transAList[index])
              .multiply(transAlphaList[index]);

            if (index == lastTotalTransList.length - 1) {
              setEndEffectorPos(new Vector3(0, 0, 0).applyMatrix4(currTrans));
            }

            return (
              <ArrowAxes
                key={index}
                position={new Vector3(0, 0, 0).applyMatrix4(currTrans)}
                revoluteMiddlePosition={new Vector3(0, 0, 0).applyMatrix4(
                  revoluteMiddlePointTrans
                )}
                type={link.type}
                rotation={new THREE.Euler().setFromRotationMatrix(currTrans)}
                lastRotation={lastTrans}
                basePosition={basePosition}
                last={index == lastTotalTransList.length - 1}
              ></ArrowAxes>
            );
          })}
      </Suspense>
    </>
  );
}

function Main() {
  const [linkList, setLinkList] = useState<linkprops[]>(
    threeLinkPlanarArmLinkList
  );
  const [startAnimation, setStartAnimation] = useState(false);
  const [animationing, setAnimationing] = useState(false);
  const [linkListSnapshot, setLinkListSnapshot] = useState<linkprops[]>([]);
  const [lastTotalTransList, setLastTotalTransList] = useState<THREE.Matrix4[]>(
    []
  );
  const [endEffectorPos, setEndEffectorPos] = useState<THREE.Vector3>(
    new Vector3(0, 0, 0)
  );

  useEffect(() => {
    if (startAnimation) {
      setLinkListSnapshot(linkList);
      setLinkList([
        {
          d: 0,
          theta: 0,
          a: 0,
          alpha: 0,
          type: linkList[0].type,
        },
      ]);
      setLastTotalTransList([]);
      setAnimationing(true);
      setStartAnimation(false);
    }
  }, [startAnimation]);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "column", md: "row" },
        height: "100%",
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Controls
          linkList={linkList}
          setLinkList={setLinkList}
          endEffectorPos={endEffectorPos}
        />
      </Box>
      <Box sx={{ flex: 2, minHeight: 600 }}>
        <Canvas>
          <Scene
            linkList={linkList}
            setLinkList={setLinkList}
            animationing={animationing}
            setAnimationing={setAnimationing}
            linkListSnapshot={linkListSnapshot}
            lastTotalTransList={lastTotalTransList}
            setLastTotalTransList={setLastTotalTransList}
            setEndEffectorPos={setEndEffectorPos}
          />
        </Canvas>
      </Box>
    </Box>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
}

function IK(props: TabPanelProps) {
  const { children, ...other } = props;

  return (
    <>
      <Main />
      <Helmet>
        <title>Visualization of Inverse Kinematics</title>

        <meta
          name="description"
          content="Explore interactive analytical solutions for inverse kinematics: Set target positions for three-link planar arm, spherical arm, anthropomorphic arm, and more. Get all possible solutions displayed in 3D. Perfect for learning and visualizing inverse kinematics in robotics."
        />
      </Helmet>
    </>
  );
}

export default IK;
